<template>
    <base-page :model="model.heroSection" content-page>
        <a v-if="model.featured" :href="model.featured.url" class="content-featured article">
            <img class="article-cover" :title="model.featured.title" :alt="model.featured.title"
                :src="model.featured.image.url"></img>
            <div class="article-summary">
                <small>{{ model.translations['articleContainer.Featured'] }}</small>
                <h2>{{ model.featured.title }}</h2>
                <span class="date">{{ longLocalDate(culture, model.featured.createDate) }}</span>
                <span>{{ model.featured.description }}</span>
            </div>
        </a>
        <div class="filters">
            <Input type="text" :placeholder="model.translations['generic.Search']" :model-value="filters.query"
                @update:model-value="updateSearch"></Input>
            <Select :modelValue="filters.year" @update:modelValue="(val) => { filters.year = Number(val) }">
                <SelectTrigger class="filters-select">
                    <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent class="max-h-[400px]">
                    <SelectGroup>
                        <SelectLabel>Years</SelectLabel>
                        <SelectItem v-for="item in availableYears" :value="item">
                            {{ item }}
                        </SelectItem>
                    </SelectGroup>
                </SelectContent>
            </Select>
        </div>
        <div class="content-articles" v-if="articles?.articles">
            <a v-for="article in articles.articles" :key="article.url" :href="article.url" class="article">
                <img class="article-cover" :title="article.title" :alt="article.title" :src="article.image.url"></img>
                <div class="article-summary">
                    <h2>{{ article.title }}</h2>
                    <span class="date">{{ longLocalDate(culture, article.createDate) }}</span>
                    <span class="article-description" v-html="article.description"></span>
                </div>
            </a>
        </div>
        <div v-else-if="!isLoading">no data</div>
        <div class="pagination">
            <Pagination :items-per-page="filters.itemsPerPage" :total="articles?.totalRows" show-edges
                v-model:page="filters.page" :sibling-count="isMobile ? 0 : 1">
                <PaginationContent v-slot="{ items }">
                    <PaginationPrevious class="pagination-button" />
                    <template v-for="(item, index) in items" :key="index">
                        <PaginationItem v-if="item.type === 'page'" :value="item.value"
                            :is-active="item.value === filters.page" class="pagination-item">
                            {{ item.value }}
                        </PaginationItem>
                        <PaginationEllipsis v-else :key="item.type" :index="index">
                            &#8230;
                        </PaginationEllipsis>
                    </template>
                    <PaginationNext class="pagination-button" />
                </PaginationContent>
            </Pagination>
            <Select :modelValue="filters.itemsPerPage"
                @update:modelValue="(val) => { filters.itemsPerPage = Number(val) }">
                <SelectTrigger class="pagination-select">
                    <SelectValue placeholder="Select pagination count" />
                </SelectTrigger>
                <SelectContent class="max-h-[400px]">
                    <SelectGroup>
                        <SelectLabel>Item count</SelectLabel>
                        <SelectItem v-for="item in ITEMS_PER_PAGE_OPTIONS" :value="item">
                            {{ item }}
                        </SelectItem>
                    </SelectGroup>
                </SelectContent>
            </Select>
        </div>
    </base-page>
</template>
<script lang="ts" setup>
import BasePage from '@/components/Common/BasePage/BasePage.vue';
import { IArticleListFilters, IArticleListProps } from './ArticleList.model';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { computed, reactive, watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { getArticles } from '@/api/article/article.api';
import { DEBOUNCE_TIME, DEFAULT_FILTERS, ITEMS_PER_PAGE_OPTIONS, YEAR_SELECT_COUNT } from './ArticleList.constant';
import { longLocalDate } from "@/share/util"
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { useDebounceFn } from '@vueuse/core'
import { parseParam } from './ArticleList.helper';

const { model } = defineProps<IArticleListProps>()

const urlParams = new URLSearchParams(window.location.search);
const filters = reactive<IArticleListFilters>({
    page: parseParam(urlParams.get("page"), DEFAULT_FILTERS.page),
    year: parseParam(urlParams.get("year"), DEFAULT_FILTERS.year),
    itemsPerPage: parseParam(urlParams.get("itemsPerPage"), DEFAULT_FILTERS.itemsPerPage),
    query: parseParam(urlParams.get("query"), DEFAULT_FILTERS.query)
});

const culture = window.__STATE__?.culture ?? ""
const currentUmbracoPageId = window.__STATE__?.currentPageId ?? 0

const { data: articles, isLoading } = useQuery({
    queryKey: ['articleList', filters],
    queryFn: () => getArticles(model.newsUrl, {
        ...filters, culture, currentUmbracoPageId
    }),
})

const availableYears = computed(() => {
    const currentYear = new Date().getFullYear()
    const years = Array.from({ length: YEAR_SELECT_COUNT }, (_, i) => currentYear - (YEAR_SELECT_COUNT - 1 - i)).sort((a, b) => b - a);
    return years
})


const updateSearch = useDebounceFn((value: string | number) => {
    filters.page = 1;
    filters.query = String(value).trim()
}, DEBOUNCE_TIME)

const isMobile = computed(() => window.matchMedia("(max-width: 768px)").matches)

watch(filters, (currentFilters) => {
    const params = new URLSearchParams();

    if (currentFilters.query) {
        params.set('query', currentFilters.query);
    } else {
        params.delete('query')
    }
    if (currentFilters.year) {
        params.set('year', String(currentFilters.year));
    }
    else {
        params.delete('year')
    }
    params.set('page', String(currentFilters.page));
    params.set('itemsPerPage', String(currentFilters.itemsPerPage));

    const queryString = params.toString();

    const newUrl = queryString
        ? `${window.location.pathname}?${queryString}`
        : window.location.pathname;

    window.history.replaceState({ path: newUrl }, '', newUrl);

}, {
    deep: true,
    immediate: true
});

watch([() => filters.year, () => filters.itemsPerPage], () => {
    filters.page = 1;
});

</script>
<style lang="scss" scoped>
.filters {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .filters-select {
        width: 100%;
    }
}

.pagination {
    margin-top: 15px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    justify-content: center;

    .pagination-select {
        width: 100%;
    }

    .pagination-item[data-selected="true"] {
        background-color: var(--primary);

        &:hover {
            background-color: var(--primary-dark);
        }

        &:active {
            background-color: var(--primary);

        }
    }

    .pagination-button,
    .pagination-item[data-selected="true"] {
        background-color: var(--primary);
        color: var(--background);

        &:hover {
            background-color: var(--primary-dark);
        }

        &:active {
            background-color: var(--primary);

        }
    }

    .pagination-button {
        cursor: pointer;
    }
}

.content-featured {
    display: flex;
}

.content-articles {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 20px;
    gap: 20px
}

.article {
    display: flex;
    flex-direction: column;
    border-radius: 24px;
    border: 1px solid lightgray;
    width: 100%;

    .article-cover {
        object-fit: cover;
        border-radius: 24px 24px 0 0;
        border-bottom: 1px solid lightgray;
        width: 100%;
        aspect-ratio: 1/1;
    }

    .article-summary {
        padding: 16px 24px;
        display: flex;
        flex-direction: column;
        gap: 4px;

        .date {
            font-weight: 300;
        }

        h2 {
            color: var(--primary);
        }

        .article-description {
            overflow: hidden;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            white-space: break-spaces;
            -webkit-line-clamp: 6;
        }
    }
}

@media (min-width: 768px) {
    .article {
        flex-direction: row;
        height: 400px;

        .article-cover {
            border-radius: 24px 0 0 24px;
            border-right: 1px solid lightgray;
            max-width: 30%;
            aspect-ratio: unset;
            flex-shrink: 0;
        }
    }

    .filters {
        flex-direction: row;

        .filters-select {
            width: 180px;
        }
    }

    .pagination {
        flex-direction: row;

        .pagination-select {
            width: 180px;
        }
    }
}
</style>